import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  Box, Card, CardActionArea, CardContent, Grid, IconButton, Paper, Stack, Typography
} from '@mui/material'
import { SubHeader } from 'components/SubHeader'
import { useAppDispatch } from 'redux/hooks'
import { initialBreadcrumb } from 'redux/reducers/breadcrumb/actions'
import { Helmet } from 'react-helmet-async'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { PieChartEngagement } from './components/PieChartEngagement'
import { BarChartFrequency, BarDataItem } from './components/BarChartFrequency'
import { AthleteRoutine, DataGridAthleteTraining } from './components/DataGridAthleteTraining'
import api from 'services/api'
import { LoadingPage } from 'components/LoadingPage'
import { DatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import ptBR from 'date-fns/locale/pt-BR'
import dayjs from 'dayjs'
import moment from 'moment'
import Iconify from 'components/Iconify'

type CustomCardProps = {
  title?: string | null
  subtitle?: number | string | null
  tooltipTitle?: string | null
}

type Item = {
  athletes: AthleteRoutine[]
  engagements: Array<{
    startDate: string
    endDate: string
    trainedDays: number
    workoutQuantity: number
  }>
  totalEngagementPercentage: number
  totalRoutines: number
  totalAthletes: number
}

function firstMondayOfTheMonth (year: number, month: number): Date {
  const previousMonth = month === 0 ? 11 : month - 1
  const previousYear = month === 0 ? year - 1 : year
  const date = new Date(previousYear, previousMonth, 1)
  const dayOfWeek = date.getDay()
  const daysToAdd = (dayOfWeek === 0) ? 1 : ((8 - dayOfWeek) % 7)
  date.setDate(date.getDate() + daysToAdd)

  return date
}

function lastSunday (data: Date = new Date()): Date {
  const firstOfMonth = new Date(data.getFullYear(), data.getMonth(), 1)
  const lastDayPrevMonth = new Date(firstOfMonth)
  lastDayPrevMonth.setDate(0)
  const dayOfWeek = lastDayPrevMonth.getDay()
  lastDayPrevMonth.setDate(lastDayPrevMonth.getDate() - dayOfWeek)

  return lastDayPrevMonth
}

function formatarData (data: Date | null) {
  if (!data) return ''
  return dayjs(data).format('DD/MM/YYYY')
}

export const Dashboard = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<Item>()
  const [formattedEngagement, setformattedEngagement] = useState<BarDataItem[]>([])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [erroStartDate, setErroStartDate] = useState('')
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [erroEndDate, setErroEndDate] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const inputRefStartDate = useRef<HTMLInputElement>(null)
  const inputRefEndDate = useRef<HTMLInputElement>(null)
  const [searchDisabled, setSearchDisabled] = useState<boolean>(false)

  useEffect(() => {
    const today = new Date()
    const firstMonday = firstMondayOfTheMonth(today.getFullYear(), today.getMonth())
    setStartDate(firstMonday)
    if (inputRef.current) {
      inputRef.current.value = formatarData(firstMonday)
    }
    const lastSundayToDay = lastSunday(today)
    setEndDate(lastSundayToDay)
    if (inputRef.current) {
      inputRef.current.value = formatarData(lastSundayToDay)
    }
  }, [])

  const allowOnlyMondays = (date: Date) => {
    return date.getDay() !== 1
  }

  useEffect(() => {
    dispatch(initialBreadcrumb({ label: t('header.title.dashboard'), path: location.pathname }))
    dataFetch().catch(console.error)
  }, [])

  useEffect(() => {
    const mapped = (data?.engagements ?? []).map((item, index) => ({
      week: (index + 1).toString() + ' sem.',
      weekFormatt: `${moment(item.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY')} a ${moment(item.endDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}`,
      trainedDays: item.trainedDays,
      workoutQuantity: item.workoutQuantity
    }))
    setformattedEngagement(mapped)
  }, [data])

  const allowOnlySunday = (date: Date) => {
    return date.getDay() !== 0
  }

  const checkDateInput = (field: string) => {
    setSearchDisabled(true)
    const ref = field === 'startDate' ? inputRefStartDate : inputRefEndDate
    const formattedDate = ref.current?.value ?? ''
    if (!formattedDate.trim()) {
      field === 'startDate'
        ? setErroStartDate(() => t<string>('general.searchDate.empty'))
        : setErroEndDate(() => t<string>('general.searchDate.empty'))
      return false
    }

    const dataMoment = moment(formattedDate.replace(/[\u2066-\u2069\u200E\u200F\u202A-\u202E]/g, '')
      .trim()
      .replace(/\s*\/\s*/g, '/'), 'DD/MM/YYYY', true)

    if (!dataMoment.isValid()) {
      field === 'startDate'
        ? setErroStartDate(() => t<string>('general.searchDate.invalid'))
        : setErroEndDate(() => t<string>('general.searchDate.invalid'))
      return false
    }

    const expectedDayOfWeek = field === 'startDate' ? 1 : 0
    if (dataMoment.day() !== expectedDayOfWeek) {
      if (field === 'startDate') {
        setErroStartDate(() => t<string>('general.searchDate.startDate'))
        return false
      }
      setErroEndDate(() => t<string>('general.searchDate.endDate'))
      return false
    }

    const isStartDate = field === 'startDate'
    const otherRef = isStartDate ? inputRefEndDate : inputRefStartDate
    const otherValue = otherRef.current?.value ?? ''

    if (otherValue.trim()) {
      const cleanedOtherDate = otherValue
        .replace(/[\u2066-\u2069\u200E\u200F\u202A-\u202E]/g, '')
        .trim()
        .replace(/\s*\/\s*/g, '/')

      const otherMoment = moment(cleanedOtherDate, 'DD/MM/YYYY', true)

      if (otherMoment.isValid()) {
        if (isStartDate && dataMoment.isAfter(otherMoment)) {
          setErroStartDate(() => t<string>('general.searchDate.startAfterEnd'))
          return false
        }

        if (!isStartDate && dataMoment.isBefore(otherMoment)) {
          setErroEndDate(() => t<string>('general.searchDate.endBeforeStart'))
          return false
        }
      }
    }

    field === 'startDate' ? setErroStartDate('') : setErroEndDate('')
    setSearchDisabled(false)
    return true
  }

  const dataFetch = async () => {
    try {
      setLoading(true)
      const startDateParam = startDate
        ? moment(startDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : moment(formatarData(firstMondayOfTheMonth(new Date().getFullYear(), new Date().getMonth())), 'DD/MM/YYYY').format('YYYY-MM-DD')
      const endDateParam = endDate
        ? moment(endDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : moment(formatarData(lastSunday(new Date())), 'DD/MM/YYYY').format('YYYY-MM-DD')
      const { data } = await api.get(`/professional-athletes/professional-athlete-dashboard?startDate=${startDateParam}&endDate=${endDateParam}`)
      setData(data)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleSearchClick = async () => {
    const startValid = checkDateInput('startDate')
    const endValid = checkDateInput('endDate')

    if (startValid && endValid) {
      await dataFetch()
    }
  }

  const CustomCard = ({ title, subtitle }: CustomCardProps) => (
    <Card sx={{ width: '100%', height: '100%' }}>
      <CardActionArea
        sx={{
          height: '100%',
          '&[data-active]': {
            backgroundColor: 'action.selected',
            '&:hover': {
              backgroundColor: 'action.selectedHover'
            }
          }
        }}
      >
        <CardContent>
          <Typography fontSize={22} color="#d6500d" fontWeight="bold">{title}</Typography>
          <Typography fontSize={33} fontWeight="bold" color="#fff">{subtitle}</Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )

  return (
    <>
      <Helmet>
        <title> {t('header.title.dashboard')} | DWF </title>
      </Helmet>
      <Box sx={{ minHeight: 200 }}>
        <SubHeader path={location.pathname} goBack={false} />
        { loading
          ? <LoadingPage />
          : <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid container spacing={2} item xs={12} sm={12}>
              <Card sx={{ minHeight: 70, width: '100%', pl: 2, pt: 2, pb: 1, alignItems: 'right', justifyContent: 'right', backgroundColor: 'transparent' }}>
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent={{ xs: 'center', sm: 'right' }}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                  width="100%"
                  minHeight={70}
                  gap={2}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Início"
                      value={startDate}
                      shouldDisableDate={allowOnlyMondays}
                      onChange={(newValue) => {
                        setStartDate(newValue)
                        if (inputRefStartDate.current) {
                          inputRefStartDate.current.value = formatarData(newValue)
                        }
                        checkDateInput('startDate')
                        checkDateInput('endDate')
                      }}
                      onClose={() => {
                        checkDateInput('startDate')
                        checkDateInput('endDate')
                      }}
                      slotProps={{
                        textField: {
                          inputRef: inputRefStartDate,
                          error: !!erroStartDate,
                          helperText: erroStartDate,
                          onBlur: () => checkDateInput('startDate'),
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === 'Escape' || e.key === 'Esc') {
                              e.preventDefault()
                              checkDateInput('startDate')
                            }
                            if (e.key === 'tab') {
                              checkDateInput('startDate')
                            }
                          },
                          inputProps: {
                            readOnly: true,
                            style: { caretColor: 'transparent' }
                          },
                          InputLabelProps: {
                            shrink: true
                          },
                          sx: {
                            width: { sm: '22%' },
                            fontSize: '14px',
                            minHeight: 75,
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '11px', sm: '16px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '20px',
                              color: '#d6500d'
                            }
                          }
                        }
                      }}
                    />
                    <DatePicker
                      label="Fim"
                      value={endDate}
                      shouldDisableDate={allowOnlySunday}
                      onChange={(newValue) => {
                        setEndDate(newValue)
                        if (inputRefEndDate.current) {
                          inputRefEndDate.current.value = formatarData(newValue)
                        }
                        checkDateInput('endDate')
                        checkDateInput('startDate')
                      }}
                      onClose={() => {
                        checkDateInput('endDate')
                        checkDateInput('startDate')
                      }}
                      slotProps={{
                        textField: {
                          inputRef: inputRefEndDate,
                          error: !!erroEndDate,
                          helperText: erroEndDate,
                          onBlur: () => checkDateInput('endDate'),
                          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === 'Escape' || e.key === 'Esc') {
                              e.preventDefault()
                              checkDateInput('endDate')
                            }
                            if (e.key === 'tab') {
                              checkDateInput('endDate')
                            }
                          },
                          inputProps: {
                            readOnly: true,
                            style: { caretColor: 'transparent' }
                          },
                          InputLabelProps: {
                            shrink: true
                          },
                          sx: {
                            width: { sm: '22%' },
                            fontSize: '18px',
                            minHeight: 75,
                            '& .MuiFormHelperText-root': {
                              fontSize: { xs: '11px', sm: '16px' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '20px',
                              color: '#d6500d'
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                  <Stack
                    direction="column"
                    sx={{ minHeight: 75 }}
                  >
                    <IconButton
                      disabled={searchDisabled}
                      onClick={handleSearchClick}
                      sx={{
                        width: { sm: 45 },
                        height: 45,
                        marginTop: 0.4,
                        borderRadius: '12px',
                        backgroundColor: '#d6500d',
                        '&:hover': {
                          backgroundColor: '#d28a66ff'
                        }
                      }}
                    >
                      <Iconify icon="fe:search" sx={{ fontSize: 30, width: 30, height: 30 }} />
                    </IconButton>
                  </Stack>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Box display="flex" flexDirection="column" height="100%" gap={2} textAlign={'center'}>
                <Box flex={1}>
                  <CustomCard title={`${t('general.searchDate.labelTotalAthletes')}`} subtitle={ data?.totalAthletes ?? 0 } />
                </Box>
                <Box flex={1}>
                  <CustomCard title={`${t('general.searchDate.labelTotalPrescribedRoutines')}`} subtitle={ data?.totalRoutines ?? 0 } />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={10}>
              <Box display="flex" gap={2} height="100%" flexDirection={{ xs: 'column', sm: 'row' }} textAlign={'center'} >
                <Box>
                  <Card sx={{ width: '100%', height: '100%' }}>
                    <PieChartEngagement totalEngagementPercentage={ data?.totalEngagementPercentage ?? 0 } />
                  </Card>
                </Box>
                <Box flex={3}>
                  <Card sx={{ width: '100%', height: '100%' }}>
                    <BarChartFrequency
                      barData={ formattedEngagement }
                    />
                  </Card>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} sx={{ minHeight: 200 }} textAlign={'center'}>
              <Paper sx={{ width: '100%' }}>
                <DataGridAthleteTraining
                  dataRows={data?.athletes}
                />
              </Paper>
            </Grid>
          </Grid>
        }
      </Box>
    </>
  )
}
